import { t } from '@avalara/bat-apps-shared/i18n';

import { SCol, SRow } from '@avalara/skylab-sdk/react';

import type { BillingContactField, BillingContactValidationErrors } from '@avalara/types/billingContact';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useHistory } from 'react-router-dom';
import type { ClientBillingContact as BillingContactData } from 'types/biztech';
import type { Address as AddressType } from '../../../../types';

import { Pages } from '../../../../types/offers';
import { getCountries, resolveAddress } from '../../lib/side-effects/avatax';
import { createBillingContact, validateBillingContactForm } from '../../lib/side-effects/billing-contact';
import { goToNextOfferStep } from '../../lib/side-effects/offer-flow';
import {
  isBillingContactFormValid as checkIsBillingContactFormValid,
  isBillingContactUnchanged as checkIsBillingContactUnchanged,
} from '../../reducers/billingContact';
import { selectState } from '../../selectors';
import {
  resetBillingContactEditForm,
  setBillingContactValidatedAddress,
  showBillingContactValidationErrors,
  updateBillingContactEditForm,
} from '../../store/billing-contact';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { setCurrentOfferPage } from '../../store/offer';
import AddressValidationDialog from '../shared/address-validation';
import Address from './address';
import Contact from './contact';

export type ContactInfoProps = {
  billingContactEditForm: BillingContactData;
  isAdmin: boolean;
  onUpdateFormField: (_: BillingContactField, __: string | null) => void;
  showValidationErrors: boolean;
  validationErrors: BillingContactValidationErrors;
};

const extractAddressFromBillingContact = (billingContactEditForm: BillingContactData): AddressType => ({
  line1: billingContactEditForm.Address1 || '',
  line2: billingContactEditForm.Address2 || '',
  line3: '',
  city: billingContactEditForm.City || '',
  postalCode: billingContactEditForm.PostalCode || '',
  country: billingContactEditForm.Country || '',
  region: billingContactEditForm.State || '',
});

export const formatBillingContactAddressFromAddress = (address: AddressType): Partial<BillingContactData> => ({
  Address1: address.line1,
  Address2: address.line2,
  City: address.city,
  PostalCode: address.postalCode,
  Country: address.country,
  State: address.region,
});

const ContactInfo = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { billingContact, account } = useSelector(selectState);
  const { isAdmin, accountId, customerAccountId } = account;

  const [fetchingResolveAddresses, setfetchingResolveAddresses] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [responseAddresses, setResponseAddresses] = useState({
    address: {
      line1: '',
      line2: '',
      line3: '',
      city: '',
      region: '',
      country: '',
      postalCode: '',
    },
    validatedAddresses: [
      {
        addressType: '',
        line1: '',
        line2: '',
        line3: '',
        city: '',
        region: '',
        country: '',
        postalCode: '',
        latitude: 0,
        longitude: 0,
      },
    ],
  });

  const { billingContactEditForm, showValidationErrors, validationErrors } = billingContact;

  const isBillingContactFormValid = checkIsBillingContactFormValid(billingContact);
  const isBillingContactUnchanged = checkIsBillingContactUnchanged(billingContact);

  useEffect(
    () => () => {
      dispatch(resetBillingContactEditForm());
    },
    [],
  );

  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.AddBillingContact));
    dispatch(getCountries());
  }, []);

  const onUpdateFormField = (field: BillingContactField, data: string | null) => {
    dispatch(updateBillingContactEditForm({ field, data }));
    dispatch(validateBillingContactForm());
  };
  const toggleDialog = () => {
    setDialogVisible(() => !dialogVisible);
  };

  const validateAddress = async () => {
    toggleDialog();
    const userInputAddress = extractAddressFromBillingContact(billingContactEditForm);
    const resolvedAddressResponse = await resolveAddress(userInputAddress, dispatch);

    if (!resolvedAddressResponse.ok) return;
    setResponseAddresses({
      address: userInputAddress,
      validatedAddresses: resolvedAddressResponse.value.validatedAddresses,
    });
    setfetchingResolveAddresses(false);
  };

  const saveBillingContact = () => {
    dispatch(
      createBillingContact({
        accountId,
        customerAccountId: customerAccountId || '',
        billingContactData: billingContactEditForm,
      }),
    );
    dispatch(goToNextOfferStep({ history }));
  };

  const handleNextClick = async () => {
    dispatch(showBillingContactValidationErrors());
    if (isBillingContactFormValid) {
      await validateAddress();
    }
  };

  const cancelDisabled = isBillingContactUnchanged;
  const saveDisabled = !isAdmin || cancelDisabled;

  return (
    <>
      <SRow class='pad-bottom-md'>
        <SCol class='pad-bottom-none'>
          <Contact
            billingContactEditForm={billingContactEditForm}
            isAdmin={isAdmin}
            onUpdateFormField={onUpdateFormField}
            showValidationErrors={showValidationErrors}
            validationErrors={validationErrors}
          />
        </SCol>
      </SRow>
      <SRow>
        <SCol>
          <Address
            billingContactEditForm={billingContactEditForm}
            isAdmin={isAdmin}
            onUpdateFormField={onUpdateFormField}
            showValidationErrors={showValidationErrors}
            validationErrors={validationErrors}
          />
        </SCol>
      </SRow>
      <hr />
      <SRow>
        <SCol class='pad-top-sm'>
          {!fetchingResolveAddresses && (
            <AddressValidationDialog
              responseAddresses={responseAddresses}
              dialogVisible={dialogVisible}
              setDialogVisible={toggleDialog}
              setPresetAddress={(address) => {
                const formattedAddress = formatBillingContactAddressFromAddress(address);
                dispatch(setBillingContactValidatedAddress({ data: formattedAddress }));
                saveBillingContact();
              }}
            />
          )}
          <button
            type='button'
            className='primary large'
            data-ref='buydot.contact-info.submit-button'
            disabled={saveDisabled}
            onClick={saveDisabled ? undefined : handleNextClick}
          >
            {t('client.buttons.next')}
          </button>
          <button
            type='button'
            className='secondary large'
            data-ref='buydot.contact-info.back-button'
            disabled={cancelDisabled}
            onClick={isBillingContactUnchanged ? undefined : () => dispatch(resetBillingContactEditForm())}
          >
            {t('client.buttons.back')}
          </button>
        </SCol>
      </SRow>
    </>
  );
};

export default ContactInfo;
